import React from 'react';
import { Button, Checkbox, Chip, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { IProductLine, PRODUCT_LABELS } from '../../../Services/productEntitlements';

interface Props {
	lines: IProductLine[];
	/** Quantités saisissables (compte sous contrat). Sinon lecture seule : Stripe possède l'info. */
	editable?: boolean;
	draft?: Record<string, string>;
	onDraftChange?: (product: string, value: string) => void;
	/** « Illimité » par produit (ligne contrat) : coché, la quantité cesse de plafonner. */
	unlimitedDraft?: Record<string, boolean>;
	onUnlimitedChange?: (product: string, value: boolean) => void;
	/** Rendre une ligne contrat à Stripe (mixte). */
	onRelease?: (product: string) => void;
}

// Le tableau des produits d'un compte : quantité (contractuelle — Stripe ou
// contrat), usage réel, illimité, source. Même rendu partout, seule l'édition change.
export const ProductLinesTable: React.FC<Props> = ({
	lines,
	editable = false,
	draft = {},
	onDraftChange,
	unlimitedDraft = {},
	onUnlimitedChange,
	onRelease,
}) => (
	<Table size="small">
		<TableHead>
			<TableRow>
				<TableCell>Produit</TableCell>
				<TableCell>Quantité</TableCell>
				<TableCell>Usage</TableCell>
				<TableCell>
					<Tooltip title="Décoché (le défaut) : la quantité convenue plafonne, le compte ne crée pas au-delà. Coché : illimité, le compte dépasse et l'écart se lit dans l'usage.">
						<span>Illimité</span>
					</Tooltip>
				</TableCell>
				<TableCell>Source</TableCell>
				{onRelease && <TableCell align="right" />}
			</TableRow>
		</TableHead>
		<TableBody>
			{lines.map((l) => {
				const meta = PRODUCT_LABELS[l.product] || { label: l.product, unit: '' };
				const unlimited = editable && l.source === 'admin' ? (unlimitedDraft[l.product] ?? l.unlimited) : !l.blocking;
				// ⚠️ « Illimité » coché : la quantité convenue devient indicative, et la
				// dépasser est exactement ce qu'on a voulu. L'écart reste affiché — c'est
				// l'intérêt de la case — mais en NEUTRE. En orange et dit « dépassé », il
				// se lit comme une alerte, et on en conclut que la case n'a pas pris.
				const beyond = l.usage !== undefined && l.quantity > 0 && l.usage > l.quantity;
				const over = beyond && !unlimited;
				return (
					<TableRow key={l.product}>
						<TableCell>
							<strong>{meta.label}</strong>
							<Typography variant="caption" display="block" color="text.secondary">
								{meta.unit}
							</Typography>
						</TableCell>
						<TableCell sx={{ width: 140 }}>
							{editable ? (
								<TextField
									type="number"
									size="small"
									inputProps={{ min: 0 }}
									value={draft[l.product] ?? String(l.quantity)}
									onChange={(e) => onDraftChange?.(l.product, e.target.value)}
								/>
							) : (
								<Typography variant="body2">
									{l.quantity > 0 ? l.quantity : unlimited ? '— (illimité)' : '— (pas d’accès)'}
								</Typography>
							)}
						</TableCell>
						<TableCell>
							<Typography variant="body2" color={over ? 'warning.main' : 'text.primary'}>
								{l.usage ?? '—'}
								{over ? ' (dépassé)' : beyond ? ' (au-delà de la quantité convenue)' : ''}
							</Typography>
						</TableCell>
						<TableCell>
							{editable && l.source === 'admin' ? (
								<Checkbox
									size="small"
									checked={unlimited}
									onChange={(e) => onUnlimitedChange?.(l.product, e.target.checked)}
									inputProps={{ 'aria-label': `Illimité ${meta.label}` }}
								/>
							) : (
								<Typography variant="body2" color="text.secondary">
									{unlimited ? 'illimité' : 'plafonné'}
								</Typography>
							)}
						</TableCell>
						<TableCell>
							<Chip size="small" label={l.source === 'admin' ? 'contrat' : 'Stripe'} color={l.source === 'admin' ? 'primary' : 'default'} />
						</TableCell>
						{onRelease && (
							<TableCell align="right">
								{l.source === 'admin' && (
									<Button size="small" onClick={() => onRelease(l.product)}>
										Rendre à Stripe
									</Button>
								)}
							</TableCell>
						)}
					</TableRow>
				);
			})}
		</TableBody>
	</Table>
);
